import {
  Button,
  Datepicker,
  DatepickerProps,
  DatePickValue,
  LayoutGrid,
  makeStyles,
  theme,
  Timepicker,
  TimepickerProps,
  Typography
} from '@archer-technologies/ui-components';
import moment, { MomentInput } from 'moment';
import * as React from 'react';
import { ArcherGridFilter } from '../../Grid.types';
import { ColumnFilterProps, DefaultPredicateFn, UseFilterComponentRefs } from './types';

export type DateColumnFilterValueType = 'startDate' | 'startTime' | 'endDate' | 'endTime';

export type DateColumnFilterReducerState = Record<DateColumnFilterValueType, unknown> & {
  lastModifiedField?: DateColumnFilterValueType;
};

type DateColumnFilterReducerAction = {
  type: DateColumnFilterValueType | 'clear';
  date: unknown;
};

const useStyles = makeStyles(() => ({
  filterContainer: {
    minWidth: 'auto'
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center'
  },
  filterWrapper: {
    padding: theme.spacing(0, 1, 0, 1)
  }
}));

export const extractFilterValues = (filter: ArcherGridFilter | null) => {
  let filterState: DateColumnFilterReducerState = {
    startDate: null,
    startTime: null,
    endDate: null,
    endTime: null
  };

  if (filter && filter.value) {
    const startDateTime = moment(filter.value.startDateTime);
    const endDateTime = moment(filter.value.endDateTime);

    filterState = {
      startDate: moment({ year: startDateTime.get('year'), month: startDateTime.get('month'), date: startDateTime.get('date') }) as any,
      startTime: moment({ hour: startDateTime.get('hour'), minute: startDateTime.get('minute') }) as any,
      endDate: moment({ year: endDateTime.get('year'), month: endDateTime.get('month'), date: endDateTime.get('date') }) as any,
      endTime: moment({ hour: endDateTime.get('hour'), minute: endDateTime.get('minute') }) as any
    };
  }

  return filterState;
};

export const combineDateTime = (date: NonNullable<unknown> | any, time: NonNullable<unknown> | any) =>
  new Date(date.get('year'), date.get('month'), date.get('date'), time.get('hour'), time.get('minute'));

export const combineFilterValues = (filterState: DateColumnFilterReducerState) => {
  const { startDate, startTime, endDate, endTime } = filterState;
  if (startDate && startTime && endDate && endTime) {
    return { startDateTime: combineDateTime(startDate, startTime), endDateTime: combineDateTime(endDate, endTime) };
  } else if (startDate && startTime) {
    return { startDateTime: combineDateTime(startDate, startTime), endDateTime: moment() };
  } else if (endDate && endTime) {
    return { startDateTime: moment(0), endDateTime: combineDateTime(endDate, endTime) };
  }
  return null;
};

export const isFilterInputEmpty = (date: unknown) => date === null;

export const checkClearButtonDisabledState = ({ startDate, startTime, endDate, endTime }: DateColumnFilterReducerState) =>
  Object.values({ startDate, startTime, endDate, endTime }).every(isFilterInputEmpty);

export const checkApplyButtonDisabledState = (
  filter: ArcherGridFilter | null,
  { startDate, startTime, endDate, endTime }: DateColumnFilterReducerState,
  invalidDateTime: boolean
) => {
  let isDisabled =
    invalidDateTime ||
    Object.values({ startDate, startTime }).some(isFilterInputEmpty) ||
    Object.values({ endDate, endTime }).some(isFilterInputEmpty);

  const combinedFilterValues = combineFilterValues({ startDate, startTime, endDate, endTime });
  if (!isDisabled && filter && combinedFilterValues) {
    const { startDateTime, endDateTime } = combinedFilterValues;
    isDisabled = moment(startDateTime).isSame(filter.value.startDateTime) && moment(endDateTime).isSame(filter.value.endDateTime);
  }

  return isDisabled;
};

const initFilterState = (filter: ArcherGridFilter | null) => extractFilterValues(filter);

// @ts-ignore
const filterStateReducer: React.Reducer<DateColumnFilterReducerState, DateColumnFilterReducerAction> = (state, action) => {
  const defaultStartTime = moment({ hour: 0, minute: 0 });
  const defaultEndTime = moment({ hour: 23, minute: 59 });
  const currentDate = moment({ hour: 0, minute: 0 });
  switch (action.type) {
    case 'startDate':
      const startDateUpdate = state.startTime
        ? { ...state, startDate: action.date, lastModifiedField: action.type }
        : { ...state, startDate: action.date, startTime: defaultStartTime, lastModifiedField: action.type };
      return startDateUpdate;
    case 'startTime':
      const startTimeUpdate = state.startDate
        ? { ...state, startTime: action.date, lastModifiedField: action.type }
        : { ...state, startTime: action.date, startDate: currentDate, lastModifiedField: action.type };
      return startTimeUpdate;
    case 'endDate':
      const endDateUpdate = state.endTime
        ? { ...state, endDate: action.date, lastModifiedField: action.type }
        : { ...state, endDate: action.date, endTime: defaultEndTime, lastModifiedField: action.type };
      return endDateUpdate;
    case 'endTime':
      const endTimeUpdate = state.endDate
        ? { ...state, endTime: action.date, lastModifiedField: action.type }
        : { ...state, endTime: action.date, endDate: currentDate, lastModifiedField: action.type };
      return endTimeUpdate;
    case 'clear':
    default:
      return initFilterState(null);
  }
};

const dateColumnIntegratedFilterPredicate: DefaultPredicateFn = (value, filter) => {
  const { startDateTime, endDateTime } = filter.value;
  return moment(value).isSameOrAfter(startDateTime) && moment(value).isSameOrBefore(endDateTime);
};

const useDateColumnFilterRefs: UseFilterComponentRefs = () => {
  const startDateFilterRef = React.useRef<HTMLInputElement>(null);
  const startTimeFilterRef = React.useRef<HTMLInputElement>(null);
  const endDateFilterRef = React.useRef<HTMLInputElement>(null);
  const endTimeFilterRef = React.useRef<HTMLInputElement>(null);
  return { childComponentRefs: { startDateFilterRef, startTimeFilterRef, endDateFilterRef, endTimeFilterRef } };
};

const DateColumnFilter = ({
  filter,
  columnName,
  onFilter,
  addClearFilterStateFn,
  removeClearFilterStateFn,
  messages,
  translate,
  dateTimeLocaleCode,
  filterComponentRefs = {}
}: ColumnFilterProps) => {
  const { filterContainer, filterLabel, filterWrapper } = useStyles();
  const [filterState, dispatch] = React.useReducer(filterStateReducer, filter, initFilterState);
  const { startDateFilterRef, startTimeFilterRef, endDateFilterRef, endTimeFilterRef } = filterComponentRefs.childComponentRefs || {};
  const dateTimeErrorProps = validateDateTime(filterState, messages);
  const invalidDateTimeRange = Object.values(dateTimeErrorProps).some(props => props.error);

  const startDateProps: DatepickerProps = {
    fullWidth: true,
    menuRef: startDateFilterRef,
    label: messages.dateFilterLabel,
    locale: dateTimeLocaleCode,
    translate,
    value: filterState.startDate as DatePickValue,
    onChange: (date: unknown) => dispatch({ type: 'startDate', date }),
    ...dateTimeErrorProps.startDate
  };

  const startTimeProps: TimepickerProps = {
    fullWidth: true,
    menuRef: startTimeFilterRef,
    label: messages.timeFilterLabel,
    locale: dateTimeLocaleCode,
    translate,
    value: filterState.startTime as DatePickValue,
    onChange: (date: unknown) => dispatch({ type: 'startTime', date }),
    ...dateTimeErrorProps.startTime
  };

  const endDateProps: DatepickerProps = {
    fullWidth: true,
    menuRef: endDateFilterRef,
    label: messages.dateFilterLabel,
    locale: dateTimeLocaleCode,
    translate,
    value: filterState.endDate as DatePickValue,
    onChange: (date: unknown) => dispatch({ type: 'endDate', date }),
    ...dateTimeErrorProps.endDate
  };

  const endTimeProps: TimepickerProps = {
    fullWidth: true,
    menuRef: endTimeFilterRef,
    label: messages.timeFilterLabel,
    locale: dateTimeLocaleCode,
    translate,
    value: filterState.endTime as DatePickValue,
    onChange: (date: unknown) => dispatch({ type: 'endTime', date }),
    ...dateTimeErrorProps.endTime
  };

  const clearButtonDisabled = checkClearButtonDisabledState(filterState);
  const applyButtonDisabled = checkApplyButtonDisabledState(filter, filterState, invalidDateTimeRange);

  const onFilterClear = clearButtonDisabled
    ? noop
    : () => {
        dispatch({ type: 'clear', date: null });
        onFilter(null);
        document.body.click();
      };
  const applyfilter = () => {
    onFilter({ value: combineFilterValues(filterState) });
    document.body.click();
  };
  const onFilterApply = applyButtonDisabled ? noop : applyfilter;

  React.useEffect(() => {
    const clearFilterState = () => dispatch({ type: 'clear', date: null });
    addClearFilterStateFn(columnName, clearFilterState);
    return () => removeClearFilterStateFn(columnName);
  }, [addClearFilterStateFn, removeClearFilterStateFn, columnName, dispatch]);

  return (
    <>
      <LayoutGrid container justify="space-between" className={filterContainer}>
        <LayoutGrid item xs={2} className={filterLabel}>
          <Typography variant="body1" color="textPrimary">
            {messages.dateTimeFilterStart}
          </Typography>
        </LayoutGrid>
        <LayoutGrid item xs={5} className={filterWrapper}>
          <Datepicker {...startDateProps} />
        </LayoutGrid>
        <LayoutGrid item xs={5} className={filterWrapper}>
          <Timepicker {...startTimeProps} />
        </LayoutGrid>
      </LayoutGrid>
      <LayoutGrid container justify="space-between" className={filterContainer}>
        <LayoutGrid item xs={2} className={filterLabel}>
          <Typography variant="body1" color="textPrimary">
            {messages.dateTimeFilterEnd}
          </Typography>
        </LayoutGrid>
        <LayoutGrid item xs={5} className={filterWrapper}>
          <Datepicker {...endDateProps} />
        </LayoutGrid>
        <LayoutGrid item xs={5} className={filterWrapper}>
          <Timepicker {...endTimeProps} />
        </LayoutGrid>
      </LayoutGrid>
      <LayoutGrid container justify="flex-end">
        <LayoutGrid item>
          <Button color="primary" variant="text" size="small" disabled={clearButtonDisabled} onClick={onFilterClear}>
            {messages.clear}
          </Button>
        </LayoutGrid>
        <LayoutGrid item>
          <Button color="primary" size="small" disabled={applyButtonDisabled} onClick={onFilterApply}>
            {messages.apply}
          </Button>
        </LayoutGrid>
      </LayoutGrid>
    </>
  );
};

/**
 * Utilities
 */
function noop() {}

type DateTimeErrorProps = Record<DateColumnFilterValueType, { error?: boolean; helperText?: string }>;

function validateDateTime({ lastModifiedField, ...filterState }: DateColumnFilterReducerState, messages: any) {
  const errorProps: DateTimeErrorProps = { startDate: {}, startTime: {}, endDate: {}, endTime: {} };

  const { startDate, endDate } = filterState;

  // If both dates (start date and end date) don't exist then return early
  if (startDate === null || endDate === null) return errorProps;

  const combinedFilterValues = combineFilterValues(filterState);

  // If the dates are exactly the same, test the times instead if both exist
  if (moment(startDate as MomentInput).isSame(endDate as MomentInput) && combinedFilterValues) {
    const { startDateTime, endDateTime } = combinedFilterValues;

    if (lastModifiedField !== 'endTime' && moment(startDateTime).isAfter(endDateTime)) {
      errorProps.startTime = { error: true, helperText: messages.startTimeAfterEndTimeError };
    }

    if (lastModifiedField !== 'startTime' && moment(endDateTime).isBefore(startDateTime)) {
      errorProps.endTime = { error: true, helperText: messages.endTimeBeforeStartTimeError };
    }
  } else {
    if (lastModifiedField !== 'endDate' && moment(startDate as MomentInput).isAfter(endDate as MomentInput)) {
      errorProps.startDate = { error: true, helperText: messages.startDateAfterEndDateError };
    }

    if (lastModifiedField !== 'startDate' && moment(endDate as MomentInput).isBefore(startDate as MomentInput)) {
      errorProps.endDate = { error: true, helperText: messages.endDateBeforeStartDateError };
    }
  }

  return errorProps;
}

export { dateColumnIntegratedFilterPredicate, useDateColumnFilterRefs, DateColumnFilter };
